import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import { KibSheet } from '@chewy/kib-overlays-react';
import { KibApplicationPortal } from '@chewy/kib-application-react';
import { KibButtonNew } from '@chewy/kib-controls-react';
import { KibField, KibTextarea } from '@chewy/kib-fields-new-react';
import { ChatWrapupSheetPageType, UserAttributes, isValidEmailFormat } from '@chewy/chat-library-common';
import styles from './ChatWrapupSheet.module.scss';
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css';
import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css';

import '@chewy/kib-overlays-styles/dist/styles/kib-sheet.css';
import '@chewy/kib-transition-styles/dist/kib-transition.css';

interface ChatWrapupSheetData {
  title?: string;
  onCancel?: () => void;
  /** Fires when the user closes the COMMENTS sheet without submitting (Cancel/back). */
  onWrapupFeedbackCancelled?: () => void;
  onSubmit?: (value: string, actionType: ChatWrapupSheetPageType) => Promise<void>;
  actionType?: ChatWrapupSheetPageType;
  isOpen: boolean;
  customerData?: UserAttributes;
  sentiment?: 'positive' | 'negative' | null;
}

interface ChatWrapupSheetProps {
  data: ChatWrapupSheetData;
}

const maxChars = 500;

const EMAIL_SUBMIT_ERROR =
  'There was a problem on our end sending your copy. Please tap Send to try again.';
const FEEDBACK_SUBMIT_ERROR =
  'There was a problem on our end sending your feedback. Please tap Submit to try again.';

const FEEDBACK_DESCRIPTION_NEGATIVE =
  "What could I have done better? I'd love to know more so I can improve.";
const FEEDBACK_DESCRIPTION_POSITIVE =
  "What did you like? I'd love to know more so I can do more of it.";
const FEEDBACK_DESCRIPTION_FALLBACK =
  'Tell us more about how this could be better. We’ll use your feedback to help improve Cai.';

function getFeedbackDescription(sentiment: ChatWrapupSheetData['sentiment']) {
  if (sentiment === 'negative') {
    return FEEDBACK_DESCRIPTION_NEGATIVE;
  }
  if (sentiment === 'positive') {
    return FEEDBACK_DESCRIPTION_POSITIVE;
  }
  return FEEDBACK_DESCRIPTION_FALLBACK;
}

function getSheetCopy(
  actionType: ChatWrapupSheetPageType | undefined,
  sentiment: ChatWrapupSheetData['sentiment'],
) {
  if (actionType === ChatWrapupSheetPageType.EMAIL) {
    return {
      title: 'Get a copy of this chat',
      description: 'Where should we send the copy?',
      inputLabel: 'Email address',
    };
  }

  return {
    title: 'Give me feedback',
    description: getFeedbackDescription(sentiment),
    inputLabel: 'Your feedback (optional)',
  };
}

export default function ChatWrapupSheet({ data }: ChatWrapupSheetProps) {
  const {
    onCancel,
    onWrapupFeedbackCancelled,
    onSubmit,
    actionType,
    isOpen,
    customerData,
    sentiment,
  } = data;

  /** After successful COMMENTS submit, sheet close must not reset Yes/No on the card. */
  const skipCommentsAbandonRef = useRef(false);

  const [inputValue, setInputValue] = useState<string>('');
  const [isError, setIsError] = useState<string | undefined>(undefined);
  const [emailSubmitAttempted, setEmailSubmitAttempted] = useState(false);

  const { title, description, inputLabel } = getSheetCopy(actionType, sentiment);
  const feedbackDescription = getFeedbackDescription(sentiment);

  const isEmail = actionType === ChatWrapupSheetPageType.EMAIL;
  const isValidEmail = Boolean(
    isEmail &&
    inputValue.trim().length > 0 &&
    isValidEmailFormat(inputValue),
  );

  const emailFieldInvalid =
    Boolean(isError) || (!isValidEmail && emailSubmitAttempted);

  const clearFormState = () => {
    setInputValue('');
    setEmailSubmitAttempted(false);
    setIsError(undefined);
  };

  useEffect(() => {
    setInputValue(
      actionType === ChatWrapupSheetPageType.EMAIL
        ? (customerData?.emailAddress ?? '')
        : '',
    );
    setEmailSubmitAttempted(false);
  }, [actionType, customerData]);

  useEffect(() => {
    if (isOpen) {
      skipCommentsAbandonRef.current = false;
    }
  }, [isOpen]);

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setInputValue(event.target.value);
    setIsError(undefined);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<boolean> => {
    event.preventDefault();

    if (isEmail && !isValidEmail) {
      setEmailSubmitAttempted(true);
      return false;
    }

    if (actionType) {
      try {
        await onSubmit?.(inputValue, actionType);
        clearFormState();
        return true;
      } catch (error) {
        console.error(error);
        if (actionType === ChatWrapupSheetPageType.EMAIL) {
          setIsError(EMAIL_SUBMIT_ERROR);
          setEmailSubmitAttempted(true);
        } else {
          setIsError(FEEDBACK_SUBMIT_ERROR);
        }

        return false;
      }
    }

    return false;
  };

  const handleClose = () => {
    const skipAbandon = skipCommentsAbandonRef.current;
    skipCommentsAbandonRef.current = false;
    const abandonedCommentsFeedback =
      actionType === ChatWrapupSheetPageType.COMMENTS && !skipAbandon;
    clearFormState();
    if (abandonedCommentsFeedback) {
      onWrapupFeedbackCancelled?.();
    }
    if (!skipAbandon) {
      onCancel?.();
    }
  };

  return (
    <KibApplicationPortal
      type="overlay"
      render={({ activate, deactivate }) => (
        <KibSheet
          open={isOpen}
          bottom={true}
          onBack={handleClose}
          onClose={handleClose}
          title={title}
          className={styles.sheet}
          onOpen={activate}
          onBeforeClose={deactivate}
        >
          <form
            onSubmit={async (event) => {
              try {
                const succeeded = await handleSubmit(event);
                if (
                  succeeded &&
                  actionType === ChatWrapupSheetPageType.COMMENTS
                ) {
                  skipCommentsAbandonRef.current = true;
                  deactivate();
                  onCancel?.();
                  return;
                }
                if (succeeded) {
                  deactivate();
                }
              } catch (error) {
                console.error(error);
              }
            }}
          >
            <div className={styles.container}>
              <p className={styles.description}>
                {isEmail ? description : feedbackDescription}
              </p>
              {isEmail ? (
                <KibField
                    type="email"
                    value={inputValue}
                    onChange={handleInputChange}
                    label={inputLabel}
                    required={true}
                    invalid={emailFieldInvalid}
                    messagesContent={
                      isError ||
                      (!isValidEmail && emailSubmitAttempted
                        ? 'Please enter a valid email address'
                        : undefined)
                    }
                    messages={{
                      valueMissing: 'Please enter your email address.',
                      typeMismatch: 'Please enter a valid email address',
                      patternMismatch: 'Please enter a valid email address',
                    }}
                    aria-invalid={emailFieldInvalid}
                  />
              )
                : (
                  <div className={styles.textareaContainer}>
                    <KibTextarea
                      value={inputValue}
                      onChange={handleInputChange}
                      label={inputLabel}
                      required={false}
                      maxLength={maxChars}
                      className={styles.textarea}
                      invalid={Boolean(isError)}
                      messagesContent={isError}
                      aria-invalid={Boolean(isError)}
                    />
                    <span className={styles.charCount}>{inputValue.length}/{maxChars}</span>
                  </div>

                )}
              <div className={styles.formButtons}>
                <KibButtonNew
                  onClick={() => {
                    handleClose();
                    deactivate();
                  }}
                  theme="action"
                  emphasis="tertiary"
                  size="medium"
                  type="button"
                  className={styles.button}
                >
                  Cancel</KibButtonNew>
                <KibButtonNew
                  theme="action"
                  emphasis="primary"
                  type="submit"
                  size="medium"
                  className={styles.button}
                >
                  {isEmail ? 'Send' : 'Submit'}</KibButtonNew>
              </div>
            </div>
          </form>
        </KibSheet>
      )}
    />
  );
}
